import axios from 'axios';
import { JWT_ACCESS_TOKEN_KEY, storeRemoveAccessToken } from './jwt-token-access';
import getAccessToken from './jwt-token-access';
import fileDownload from 'js-file-download';

// Base URL
export const API_URL = process.env.REACT_APP_URL_STAGING;
export const ISLIVE_MERCHANT = process.env.REACT_APP_TITAN_MERCHANT_ISLIVE;

const axiosApi = axios.create({
  baseURL: API_URL,
});

// redirect ke halaman login
function redirectToLogin() {
  storeRemoveAccessToken();
  window.location.replace('/login');
}

// Interceptor request
axiosApi.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem(JWT_ACCESS_TOKEN_KEY);
    config.headers = {
      ...config.headers,
      Authorization: token ? `${token}` : '',
      'Is-Live': `${ISLIVE_MERCHANT}`,
    };
    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor response: token refresh when unauthorized or 401
axiosApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const resultAccessToken = localStorage.getItem(JWT_ACCESS_TOKEN_KEY);
        const response = await axios.post(`${API_URL}/authenticate/access-token`, {
          access_token: resultAccessToken,
        });
        const { access_token } = response.data;
        localStorage.setItem(JWT_ACCESS_TOKEN_KEY, access_token);
        axiosApi.defaults.headers.common['Authorization'] = `${access_token}`;
        return axiosApi(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem(JWT_ACCESS_TOKEN_KEY);
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

// Handle error response
export const handleErrorRequest = (error) => {
  if (error.detail) {
    return error.detail;
  } else if (error.response) {
    if (error.response.data.errors) {
      let errors = error.response.data.errors.map((error) => error.detail);
      if (errors.length > 0) {
        return errors.join('\n');
      }
    }
    return error.response.data;
  } else if (error.request) {
    if (error.request.status === 404) {
      return error.request.statusText;
    }
    if (error.request.status === 0) {
      return 'Internal Server Error';
    }
    return error.request.statusText;
  }
  return 'Internal Server Error';
};

// Fungsi HTTP GET
export async function get(url, config = {}) {
  return await axiosApi
    .get(url, config)
    .then((response) => response.data)
    .catch((error) => {
      throw { detail: handleErrorRequest(error), status_code: error?.request?.status };
    });
}

// Fungsi HTTP GET untuk download CSV
export async function getCsv(url, fileName, config = {}) {
  return await axiosApi
    .get(url, { ...config, responseType: 'blob' })
    .then((response) => {
      const blob = new Blob([response.data], { type: 'text/csv' });
      fileDownload(blob, fileName);
      return { success: true };
    })
    .catch((error) => {
      throw { detail: handleErrorRequest(error), status_code: error?.request?.status };
    });
}

// Fungsi HTTP POST
export async function post(url, data, config = {}) {
  return axiosApi
    .post(url, data, config)
    .then((response) => response.data)
    .catch((error) => {
      throw { detail: handleErrorRequest(error), status_code: error?.request?.status };
    });
}

// Fungsi HTTP PUT
export async function put(url, data, config = {}) {
  return axiosApi
    .put(url, data, config)
    .then((response) => response.data)
    .catch((error) => {
      throw { detail: handleErrorRequest(error), status_code: error?.request?.status };
    });
}

// Fungsi HTTP DELETE
export async function del(url, config = {}) {
  return await axiosApi
    .delete(url, config)
    .then((response) => response.data)
    .catch((error) => {
      throw { detail: handleErrorRequest(error), status_code: error?.request?.status };
    });
}

// Fungsi HTTP PATCH
export async function patch(url, data, config = {}) {
  return axiosApi
    .patch(url, data, config)
    .then((response) => response.data)
    .catch((error) => {
      throw { detail: handleErrorRequest(error), status_code: error?.request?.status };
    });
}

// Fungsi untuk upload file
export async function uploadFile(url, file, setProgress) {
  const formData = new FormData();
  formData.append('file', file);

  return await axiosApi
    .post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setProgress(percentCompleted);
      },
    })
    .catch((error) => {
      throw handleErrorRequest(error);
    });
}

// Fungsi untuk upload FormData
export async function uploadFormData(url, data) {
  return await axiosApi
    .post(url, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((response) => response.data)
    .catch((error) => {
      throw { detail: handleErrorRequest(error), status_code: error?.request?.status };
    });
}

// Fungsi untuk upload FormData dengan JSON
export async function uploadFormDataJson(url, data) {
  return await axiosApi
    .post(url, data, {
      headers: { 'Content-Type': 'application/json' },
    })
    .then((response) => response.data)
    .catch((error) => {
      throw { detail: handleErrorRequest(error), status_code: error?.request?.status };
    });
}

// Fungsi untuk update FormData dengan JSON
export async function updateFormDataJson(url, data) {
  return await axiosApi
    .put(url, data, {
      headers: { 'Content-Type': 'application/json' },
    })
    .then((response) => response.data)
    .catch((error) => {
      throw { detail: handleErrorRequest(error), status_code: error?.request?.status };
    });
}

// Fungsi untuk download file
export async function getDownload(url) {
  return await axiosApi
    .get(url, { responseType: 'blob' })
    .then((response) => response)
    .catch((error) => {
      throw { detail: handleErrorRequest(error), status_code: error?.request?.status };
    });
}

export async function patchFormDataJson(url, data) {
  return await axiosApi
    .patch(url, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: getAccessToken(),
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error.request.status,
      };
      throw errorDetail;
    });
}

export async function patchFormData(url, data) {
  return await axiosApi
    .patch(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: getAccessToken(),
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error.request.status,
      };
      throw errorDetail;
    });
}
